import directive from 'mdast-util-directive'
import fromMarkdown from 'mdast-util-from-markdown'
import syntax from 'micromark-extension-directive'
import * as React from 'react'

import { styled } from '~/styled'

import { MarkdownCode } from './components/MarkdownCode'
import { MarkdownEmphasis } from './components/MarkdownEmphasis'
import { MarkdownHeading } from './components/MarkdownHeading'
import { MarkdownImage } from './components/MarkdownImage'
import { MarkdownInlineCode } from './components/MarkdownInlineCode'
import { MarkdownLink } from './components/MarkdownLink'
import { MarkdownList } from './components/MarkdownList'
import { MarkdownListItem } from './components/MarkdownListItem'
import { MarkdownParagraph } from './components/MarkdownParagraph'
import { MarkdownStrong } from './components/MarkdownStrong'
import { MarkdownText } from './components/MarkdownText'
import { MarkdownThematicBreak } from './components/MarkdownThematicBreak'

type HandleNode = (node) => React.JSX.Element | null

const StyledMarkdownContent = styled('div', {
  base: [
    'flex',
    'flex-col',
    'gap-8',
    '[&>h1]:max-w-[65ch]',
    '[&>h2]:max-w-[65ch]',
    '[&>h3]:max-w-[65ch]',
    '[&>h4]:max-w-[65ch]',
    '[&>p]:max-w-[75ch]',
    '[&>ul]:max-w-[75ch]'
  ]
})

type MarkdownContentProps = React.ComponentProps<
  typeof StyledMarkdownContent
> & {
  content: string
  customComponents?: {
    [key: string]: (props: {
      node: any
      handleNode: HandleNode
    }) => React.JSX.Element
  }
}

const defaultComponentsMap = {
  code: MarkdownCode,
  emphasis: MarkdownEmphasis,
  heading: MarkdownHeading,
  inlineCode: MarkdownInlineCode,
  image: MarkdownImage,
  link: MarkdownLink,
  list: MarkdownList,
  listItem: MarkdownListItem,
  paragraph: MarkdownParagraph,
  strong: MarkdownStrong,
  text: MarkdownText,
  thematicBreak: MarkdownThematicBreak
}

const generateNodeKey = (node) => {
  if (node.position?.start) {
    const { line, column, offset } = node.position.start
    return `${node.type}${line}${column}${offset}`
  }

  return `${node.type}${+new Date()}`
}

export const MarkdownContent = ({
  content,
  customComponents = {},
  className
}: MarkdownContentProps) => {
  const AST = fromMarkdown(content, {
    extensions: [syntax()],
    mdastExtensions: [directive.fromMarkdown]
  })

  const componentsMap = {
    ...defaultComponentsMap,
    ...customComponents
  }

  const handleNode: HandleNode = (node) => {
    const MarkdownComponent = componentsMap[node.type]

    return MarkdownComponent ? (
      <MarkdownComponent
        key={generateNodeKey(node)}
        node={node}
        handleNode={handleNode}
      />
    ) : null
  }

  return (
    <StyledMarkdownContent className={className}>
      {AST.children.map(handleNode)}
    </StyledMarkdownContent>
  )
}

MarkdownContent.displayName = 'MarkdownContent'
